import api from './api';

// Simple in-memory cache with TTL
class ApiCache {
    constructor(ttl = 5 * 60 * 1000) { // 5 minutes default TTL
        this.cache = new Map();
        this.ttl = ttl;
    }

    get(key) {
        const item = this.cache.get(key);
        if (!item) return null;
        
        if (Date.now() > item.expiry) {
            this.cache.delete(key);
            return null;
        }
        
        return item.data;
    }

    set(key, data) {
        this.cache.set(key, {
            data,
            expiry: Date.now() + this.ttl
        });
    }

    clear() {
        this.cache.clear();
    }

    delete(key) {
        this.cache.delete(key);
    }
}

// Create cache instances
const vendorCache = new ApiCache(10 * 60 * 1000); // 10 minutes for vendors
const productCache = new ApiCache(5 * 60 * 1000);  // 5 minutes for products
const categoryCache = new ApiCache(30 * 60 * 1000); // 30 minutes for categories

// Optimized API methods
export const optimizedApi = {
    // Get vendor with caching
    async getVendor(id) {
        const cacheKey = `vendor-${id}`;
        const cached = vendorCache.get(cacheKey);
        
        if (cached) {
            console.log('Vendor loaded from cache:', id);
            return { data: cached };
        }
        
        console.log('Fetching vendor from API:', id);
        const response = await api.get(`/Vendor/${id}`);
        vendorCache.set(cacheKey, response.data);
        return response;
    },

    // Get vendor products with caching
    async getVendorProducts(vendorId) {
        const cacheKey = `products-${vendorId}`;
        const cached = productCache.get(cacheKey);
        
        if (cached) {
            console.log('Products loaded from cache:', vendorId);
            return { data: cached };
        }
        
        console.log('Fetching products from API:', vendorId);
        const response = await api.get(`/Vendor/${vendorId}/products`);
        productCache.set(cacheKey, response.data);
        return response;
    },

    // Get vendor categories with caching
    async getVendorCategories() {
        const cacheKey = 'vendor-categories';
        const cached = categoryCache.get(cacheKey);
        
        if (cached) {
            console.log('Vendor categories loaded from cache');
            return { data: cached };
        }
        
        console.log('Fetching vendor categories from API');
        const response = await api.get('/VendorCategory');
        categoryCache.set(cacheKey, response.data);
        return response;
    },

    // Get product categories with caching
    async getProductCategories() {
        const cacheKey = 'product-categories';
        const cached = categoryCache.get(cacheKey);
        
        if (cached) {
            console.log('Product categories loaded from cache');
            return { data: cached };
        }
        
        console.log('Fetching product categories from API');
        const response = await api.get('/ProductCategory');
        categoryCache.set(cacheKey, response.data);
        return response;
    },

    // Get product categories for a specific vendor
    async getVendorProductCategories(vendorId) {
        const cacheKey = `product-categories-vendor-${vendorId}`;
        const cached = categoryCache.get(cacheKey);
        
        if (cached) {
            console.log(`Vendor ${vendorId} product categories loaded from cache`);
            return { data: cached };
        }
        
        console.log(`Fetching product categories for vendor ${vendorId} from API`);
        const response = await api.get(`/Vendor/${vendorId}/product-categories`);
        categoryCache.set(cacheKey, response.data);
        return response;
    },

    // Get standalone product categories (not tied to any vendor)
    async getStandaloneProductCategories() {
        const cacheKey = 'standalone-product-categories';
        const cached = categoryCache.get(cacheKey);
        
        if (cached) {
            console.log('Standalone product categories loaded from cache');
            return { data: cached };
        }
        
        console.log('Fetching standalone product categories from API');
        const response = await api.get('/ProductCategory/standalone');
        categoryCache.set(cacheKey, response.data);
        return response;
    },

    // Clear cache when data is modified
    clearVendorCache(vendorId) {
        vendorCache.delete(`vendor-${vendorId}`);
        productCache.delete(`products-${vendorId}`);
        categoryCache.delete(`product-categories-vendor-${vendorId}`); // Clear vendor-specific categories
    },

    clearCategoryCache() {
        categoryCache.delete('vendor-categories');
        categoryCache.delete('product-categories');
        categoryCache.delete('standalone-product-categories');
        // Clear all vendor-specific product categories
        for (let key of categoryCache.cache.keys()) {
            if (key.startsWith('product-categories-vendor-')) {
                categoryCache.delete(key);
            }
        }
    },

    // Pass-through methods for mutations (no caching)
    async post(url, data, config) {
        const response = await api.post(url, data, config);
        
        // Clear relevant caches after mutations
        if (url.includes('/products')) {
            const vendorId = url.match(/\/vendors\/(\d+)\//)?.[1];
            if (vendorId) this.clearVendorCache(vendorId);
        } else if (url.includes('/Vendor')) {
            const vendorId = url.match(/\/Vendor\/(\d+)/)?.[1];
            if (vendorId) this.clearVendorCache(vendorId);
        } else if (url.includes('Category')) {
            this.clearCategoryCache();
        }
        
        return response;
    },

    async put(url, data, config) {
        const response = await api.put(url, data, config);
        
        // Clear relevant caches after mutations
        if (url.includes('/products')) {
            const vendorId = url.match(/\/vendors\/(\d+)\//)?.[1];
            if (vendorId) this.clearVendorCache(vendorId);
        } else if (url.includes('/Vendor')) {
            const vendorId = url.match(/\/Vendor\/(\d+)/)?.[1];
            if (vendorId) this.clearVendorCache(vendorId);
        } else if (url.includes('Category')) {
            this.clearCategoryCache();
        }
        
        return response;
    },

    async delete(url, config) {
        const response = await api.delete(url, config);
        
        // Clear relevant caches after mutations
        if (url.includes('/products')) {
            const vendorId = url.match(/\/vendors\/(\d+)\//)?.[1];
            if (vendorId) this.clearVendorCache(vendorId);
        } else if (url.includes('/Vendor')) {
            const vendorId = url.match(/\/Vendor\/(\d+)/)?.[1];
            if (vendorId) this.clearVendorCache(vendorId);
        } else if (url.includes('Category')) {
            this.clearCategoryCache();
        }
        
        return response;
    }
};

export default optimizedApi;
